import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Card,
    Stack,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Pagination,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllByType, deleteLocal } from '../../services/unifiedFormService';

const TYPE = 'employee_warning_notice';

export default function EmployeeWarningNoticeList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all|draft|submitted
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const rows = await getAllByType(TYPE);
            setItems(rows);
        } catch (e) {
            // ignore
        } finally {
            setLoading(false);
        }
    }

    const filtered = useMemo(() => {
        // Only show draft forms, hide submitted
        let rows = items.filter(r => (r.status || r.payload?.status) !== 'submitted');
        if (filter !== 'all') rows = rows.filter(r => (r.status || r.payload?.status) === filter);
        return rows;
    }, [items, filter]);

    const statusCounts = useMemo(() => {
        const drafts = items.filter(r => (r.status || r.payload?.status) !== 'submitted');
        return {
            all: drafts.length,
            draft: drafts.length,
            submitted: 0,
        };
    }, [items]);

    // Pagination calculations
    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filtered.slice(startIndex, endIndex);
    }, [filtered, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filter]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1);
    };

    const cardTitle = (rec) => {
        const p = rec.payload || {};
        const employeeName = p.employeeName || '';
        const date = p.date || '';
        if (employeeName) return `${employeeName}${date ? ' - ' + date : ''}`;
        return rec.uuid;
    };

    const getWarningNumber = (rec) => {
        const p = rec.payload || {};
        const wn = p.warningNumber || {};
        const nums = [];
        if (wn.warning1) nums.push('Warning 1');
        if (wn.warning2) nums.push('Warning 2');
        if (wn.warning3) nums.push('Warning 3');
        return nums.length > 0 ? nums.join(', ') : 'N/A';
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Employee Warning Notice</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/forms/employee-warning-notice')}
                        sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                    >
                        New Form
                    </Button>
                </Box>
                <ToggleButtonGroup value={filter} exclusive onChange={(e, v) => v && setFilter(v)} fullWidth size="small">
                    <ToggleButton value="all">All ({statusCounts.all})</ToggleButton>
                    <ToggleButton value="draft">Draft ({statusCounts.draft})</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* List */}
            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}>
                        <Typography>Loading...</Typography>
                    </Card>
                ) : filtered.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No forms found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Click "New Form" to create one</Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedItems.map((rec) => {
                            const p = rec.payload || {};
                            const status = rec.status || p.status;
                            return (
                                <Card key={rec.uuid} sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>
                                                    {cardTitle(rec)}
                                                </Typography>
                                                <Chip
                                                    label={status === 'submitted' ? 'Submitted' : 'Draft'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: status === 'submitted' ? '#4caf50' : '#9e9e9e',
                                                        color: 'white',
                                                        height: 20
                                                    }}
                                                />
                                            </Box>
                                            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    <strong>Warning:</strong> {getWarningNumber(rec)}
                                                </Typography>
                                                {p.employeeId && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>ID:</strong> {p.employeeId}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/forms/employee-warning-notice?uuid=${encodeURIComponent(rec.uuid)}`)}
                                                color="primary"
                                                disabled={status === 'submitted'}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={async () => {
                                                    if (window.confirm('Delete this form?')) {
                                                        await deleteLocal(rec.uuid);
                                                        await load();
                                                    }
                                                }}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Card>
                            );
                        })}
                    </Stack>

                    {/* Pagination Controls */}
                    {filtered.length > itemsPerPage && (
                        <Box sx={{
                            mt: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
                                </Typography>
                                <FormControl size="small">
                                    <Select
                                        value={itemsPerPage}
                                        onChange={handleItemsPerPageChange}
                                        sx={{ minWidth: 80 }}
                                    >
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="medium"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                    </>
                )}
            </Box>
        </Box>
    );
}
